import Phaser from 'phaser';
import { PALETTE } from '../config';
import { AudioEngine } from '../game/AudioEngine';
import { DISCIPLINES, DISCIPLINE_CATEGORY } from '../game/data/disciplines';
import { t, disciplineLabel, comingSoonGames } from '../game/i18n';
import { SaveManager } from '../game/SaveManager';
import { addBackButton } from '../ui/BackButton';
import { drawGradientBackground } from '../ui/Background';
import { Button } from '../ui/Button';
import { viewport } from '../ui/viewport';

/** Fallback glyph per discipline when no bespoke card art is present. */
const DISCIPLINE_EMOJI: Record<string, string> = {
  sprint_100m: '🏃', long_jump: '🦘', hurdles: '🏃', javelin: '🎯', high_jump: '🤸',
  triple_jump: '🦘', shot_put: '🏋️', hammer_throw: '🔨', pole_vault: '🐱', discus: '🥏', weightlifting: '🏋️',
  swim_50m: '🏊', diving: '🤿', archery: '🏹', _soon: '✨',
};

interface Entry { id: string; label: string; stat: string; locked: boolean; }
interface Slot { container: Phaser.GameObjects.Container; baseX: number; }

/** Category filter badges shown above the carousel. */
const FILTERS: { id: string; emoji: string }[] = [
  { id: 'all', emoji: '🏅' },
  { id: 'run', emoji: '🏃' },
  { id: 'jump', emoji: '🤸' },
  { id: 'throw', emoji: '🥏' },
  { id: 'strength', emoji: '🏋️' },
  { id: 'water', emoji: '🏊' },
  { id: 'precision', emoji: '🏹' },
];

/**
 * Discipline picker as a swipe carousel: one big focused card with the neighbours peeking,
 * drag / arrows / dots to browse, a SCEGLI button to confirm. Scales to any number of
 * events (a grid shrank every card to nothing once there were nine). Mobile-first landscape.
 */
export class DisciplineSelect extends Phaser.Scene {
  private entries: Entry[] = [];
  private selectedIndex = 0;
  private filter = 'all';
  private slots: Slot[] = [];
  private dragDX = 0;
  private dragging = false;
  private startX = 0;
  private switching = false;

  constructor() { super('DisciplineSelect'); }

  init(data: { selectedIndex?: number; filter?: string }): void {
    if (typeof data?.selectedIndex === 'number') this.selectedIndex = data.selectedIndex;
    if (data?.filter) this.filter = data.filter;
  }

  create(): void {
    this.slots = [];
    this.dragDX = 0;
    this.dragging = false;
    this.switching = false;

    const { w, h, cx, s } = viewport(this);
    drawGradientBackground(this);

    let unlocked = SaveManager.instance.get().unlockedDisciplines.filter((id) => DISCIPLINES[id]);
    if (this.filter !== 'all') unlocked = unlocked.filter((id) => DISCIPLINE_CATEGORY[id] === this.filter);
    this.entries = unlocked.map((id) => ({ id, label: disciplineLabel(id), stat: DISCIPLINES[id]!.primaryStat, locked: false }));
    // The "coming soon" card (with the upcoming-games list) only on the unfiltered view.
    if (this.filter === 'all') this.entries.push({ id: '_soon', label: t('sel.comingSoon'), stat: '', locked: true });
    this.selectedIndex = Phaser.Math.Clamp(this.selectedIndex, 0, Math.max(0, this.entries.length - 1));

    addBackButton(this, s, () => this.scene.start('MainMenu'));
    this.add.text(cx, h * 0.07, t('sel.chooseEvent'), {
      fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.round(30 * s)}px`, color: '#f4f7ff', fontStyle: 'bold',
    }).setOrigin(0.5).setShadow(0, 3, '#000000aa', 6);
    this.add.text(cx, h * 0.07 + 30 * s, t('sel.dragOrArrows'), {
      fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.round(15 * s)}px`, color: '#9fb0e0',
    }).setOrigin(0.5);

    // Category filter badges between the title and the carousel.
    this.drawFilters(cx, h * 0.17, s);

    this.buildSlots();

    // Navigation chrome — vertically centred on the focused card.
    const cardCy = this.cardSize().cy;
    this.arrow(Math.max(42, w * 0.06), cardCy, -1, s);
    this.arrow(Math.min(w - 42, w * 0.94), cardCy, 1, s);

    const cur = this.entries[this.selectedIndex]!;
    const btnY = h - Math.max(46, 52 * s), btnH = Math.max(58, 66 * s);
    this.dots(cx, btnY - btnH / 2 - 18 * s, s); // clearly above the button, no overlap
    new Button(this, cx, btnY, cur.locked ? t('sel.soon') : t('sel.choose'), () => this.choose(), {
      width: Math.min(320, w * 0.6), height: btnH, fontSize: Math.round(26 * s),
      color: cur.locked ? PALETTE.trackLine : PALETTE.laneAccent, icon: cur.locked ? undefined : 'play',
    });

    this.setupDrag(w);
  }

  private cardSize(): { cardW: number; cardH: number; cy: number } {
    const { w, h, s } = viewport(this);
    // Fit the focused card INTO the band between the filters and the choose button (+ dots), so on
    // short landscape phones the big card never overlaps the button. Derive the card from that band.
    const btnY = h - Math.max(46, 52 * s), btnH = Math.max(58, 66 * s);
    const bandTop = h * 0.21;
    const bandBottom = btnY - btnH / 2 - Math.max(26, 30 * s);
    const bandH = Math.max(120, bandBottom - bandTop);
    const labelStrip = Math.max(74, Math.round(92 * s));
    const cardW = Math.min(330, w * 0.42, Math.max(120, bandH - labelStrip + 24));
    const cardH = (cardW - 24) + labelStrip;
    const cy = bandTop + cardH / 2;
    return { cardW, cardH, cy };
  }

  private buildSlots(): void {
    const { w, cx } = viewport(this);
    const n = this.entries.length;
    const { cy } = this.cardSize();
    // Three depth tiers so the carousel has perspective: each step out is smaller, dimmer and
    // pushed toward the edge (the far pair just peeks behind the neighbours).
    const tierX = [0, w * 0.27, w * 0.44];
    const tierScale = [1, 0.62, 0.42];
    const tierA = [1, 0.5, 0.28];
    const tierD = [6, 4, 2];
    const make = (offset: -2 | -1 | 0 | 1 | 2): void => {
      const idx = (this.selectedIndex + offset + n) % n;
      const lvl = Math.abs(offset);
      const x = cx + Math.sign(offset) * tierX[lvl]!;
      const container = this.makeCard(this.entries[idx]!, x, cy, lvl === 0);
      container.setScale(tierScale[lvl]!).setAlpha(tierA[lvl]!).setDepth(tierD[lvl]!);
      this.slots.push({ container, baseX: x });
    };
    if (n > 4) { make(-2); make(2); }
    if (n > 1) { make(-1); make(1); }
    make(0);
  }

  private makeCard(entry: Entry, x: number, y: number, focused: boolean): Phaser.GameObjects.Container {
    const s = viewport(this).s;
    const { cardW, cardH } = this.cardSize();
    const r = 24;
    const c = this.add.container(x, y);
    const cc = Phaser.Display.Color.IntegerToColor(entry.locked ? 0x1a2238 : PALETTE.bgTop);

    const g = this.add.graphics();
    // Focused card gets a soft accent GLOW, not a thick orange border.
    if (focused && !entry.locked) g.fillStyle(PALETTE.laneAccent, 0.22).fillRoundedRect(-cardW / 2 - 6, -cardH / 2 - 4, cardW + 12, cardH + 16, r + 6);
    g.fillStyle(0x000000, 0.34).fillRoundedRect(-cardW / 2, -cardH / 2 + 8, cardW, cardH, r); // shadow
    g.fillStyle(cc.clone().darken(6).color, entry.locked ? 0.85 : 1).fillRoundedRect(-cardW / 2, -cardH / 2, cardW, cardH, r);
    g.fillStyle(cc.clone().lighten(8).color, entry.locked ? 0.35 : 0.85).fillRoundedRect(-cardW / 2, -cardH / 2, cardW, cardH * 0.5, r); // top sheen
    // Refined rim: a soft white edge (plus the accent glow above for focus), not a harsh orange line.
    g.lineStyle(2, focused && !entry.locked ? 0xffffff : PALETTE.cadence, focused ? 0.45 : 0.22).strokeRoundedRect(-cardW / 2, -cardH / 2, cardW, cardH, r);
    g.lineStyle(1.5, 0xffffff, 0.12).strokeRoundedRect(-cardW / 2 + 2, -cardH / 2 + 2, cardW - 4, cardH - 4, r - 2);
    c.add(g);

    const art = cardW - 24;
    const artCy = -cardH / 2 + art / 2 + 12;
    const key = `card_${entry.id}`;
    if (!entry.locked && this.textures.exists(key)) {
      const img = this.add.image(0, artCy, key).setOrigin(0.5);
      img.setScale(art / img.width);
      const mask = this.make.graphics({ x: 0, y: 0 });
      mask.fillStyle(0xffffff).fillRoundedRect(x - art / 2, y + artCy - art / 2, art, art, r - 8);
      img.setMask(mask.createGeometryMask());
      c.add(img);
      const border = this.add.graphics();
      border.lineStyle(2, 0xffffff, 0.18).strokeRoundedRect(-art / 2, artCy - art / 2, art, art, r - 8);
      c.add(border);
    } else if (entry.id === '_soon') {
      // The coming-soon card lists the next planned disciplines.
      const games = comingSoonGames();
      const headFs = Math.max(12, Math.round(15 * s));
      const lineFs = Math.max(12, Math.round(16 * s));
      const head = this.add.text(0, artCy - art * 0.42, t('sel.upcomingTitle'), {
        fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${headFs}px`, color: '#9fb0e0', fontStyle: 'bold',
      }).setOrigin(0.5, 0);
      c.add(head);
      const top = artCy - art * 0.42 + headFs + 10 * s;
      const lineH = (artCy + art * 0.46 - top) / Math.max(games.length, 1);
      const maxW = art - 16;
      games.forEach((gm, i) => {
        const ln = this.add.text(-art * 0.44, top + i * lineH + lineH / 2, `${gm.emoji}  ${gm.label}`, {
          fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${lineFs}px`, color: '#dfe7ff', fontStyle: 'bold',
        }).setOrigin(0, 0.5);
        if (ln.width > maxW) ln.setScale(maxW / ln.width);
        c.add(ln);
      });
    } else {
      const disc = this.add.circle(0, artCy, art * 0.4, PALETTE.bgBottom, 0.5).setStrokeStyle(2, PALETTE.cadence, 0.35);
      c.add(disc);
      c.add(this.add.text(0, artCy, DISCIPLINE_EMOJI[entry.id] ?? '🏅', { fontSize: `${Math.round(art * 0.42)}px` }).setOrigin(0.5));
    }

    // Title — single line near the top of the label strip (clear of the bottom border),
    // auto-shrunk so it always fits on one line.
    const labelTop = -cardH / 2 + 12 + art;
    const titleY = labelTop + Math.max(14, 18 * s);
    const title = this.add.text(0, titleY, entry.label, {
      fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.max(16, Math.round(21 * s))}px`, color: entry.locked ? '#8893bc' : '#f4f7ff', fontStyle: 'bold',
    }).setOrigin(0.5, 0);
    const maxTW = cardW - 34;
    if (title.width > maxTW) title.setScale(maxTW / title.width);
    c.add(title);

    // Clean stat chip (no emoji): the primary stat in a glossy cadence pill, below the title.
    if (entry.stat) {
      const chy = titleY + title.displayHeight + Math.max(14, 18 * s);
      const ct = this.add.text(0, chy, t(`stat.${entry.stat}`).toUpperCase(), {
        fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.max(12, Math.round(13 * s))}px`, color: '#06202b', fontStyle: 'bold',
      }).setOrigin(0.5);
      const cw = ct.width + 22 * s, ch = ct.height + 8 * s;
      const cg = this.add.graphics();
      cg.fillStyle(PALETTE.cadence, 1).fillRoundedRect(-cw / 2, chy - ch / 2, cw, ch, ch / 2);
      cg.fillStyle(0xffffff, 0.22).fillRoundedRect(-cw / 2 + 2, chy - ch / 2 + 2, cw - 4, ch * 0.45, ch / 2);
      c.add(cg);
      c.add(ct);
    }
    return c;
  }

  /** Row of category filter badges; tapping one re-renders the carousel for that category. */
  private drawFilters(cx: number, y: number, s: number): void {
    const fs = Math.max(12, Math.round(14 * s));
    const hh = Math.max(30, 34 * s);
    const padX = 12 * s, gap = Math.max(6, 8 * s);
    const items = FILTERS.map((f) => {
      const label = `${f.emoji} ${t(`cat.${f.id}`)}`;
      const probe = this.add.text(-999, -999, label, { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${fs}px`, fontStyle: 'bold' });
      const w = probe.width + padX * 2;
      probe.destroy();
      return { f, label, w };
    });
    const rawW = items.reduce((a, b) => a + b.w, 0) + gap * (items.length - 1);
    const sc = rawW > this.scale.width * 0.96 ? (this.scale.width * 0.96) / rawW : 1;
    let x = cx - (rawW * sc) / 2;
    for (const it of items) {
      const w = it.w * sc;
      const active = this.filter === it.f.id;
      const g = this.add.graphics().setDepth(8);
      g.fillStyle(active ? PALETTE.laneAccent : PALETTE.ink, active ? 0.95 : 0.5).fillRoundedRect(x, y - hh / 2, w, hh, hh / 2);
      g.lineStyle(1.5, active ? PALETTE.white : PALETTE.cadence, active ? 0.6 : 0.3).strokeRoundedRect(x, y - hh / 2, w, hh, hh / 2);
      this.add.text(x + w / 2, y, it.label, { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${fs}px`, color: active ? '#0b1020' : '#cfd8f5', fontStyle: 'bold' }).setOrigin(0.5).setDepth(9).setScale(sc);
      if (!active) {
        g.setInteractive(new Phaser.Geom.Rectangle(x, y - hh / 2, w, hh), Phaser.Geom.Rectangle.Contains)
          .on(Phaser.Input.Events.POINTER_DOWN, () => { AudioEngine.tap(0.3); this.scene.restart({ filter: it.f.id, selectedIndex: 0 }); });
      }
      x += w + gap * sc;
    }
  }

  private arrow(x: number, y: number, dir: number, s: number): void {
    const r = Math.max(28, 32 * s);
    const c = this.add.circle(x, y, r, PALETTE.ink, 0.45).setStrokeStyle(2, PALETTE.cadence, 0.7).setDepth(9);
    const t = this.add.text(x, y, dir < 0 ? '‹' : '›', { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.round(40 * s)}px`, color: '#59d2ff', fontStyle: 'bold' }).setOrigin(0.5).setDepth(9);
    [c, t].forEach((o) => o.setInteractive({ useHandCursor: true }).on(Phaser.Input.Events.POINTER_DOWN, () => this.step(dir)));
  }

  private dots(cx: number, y: number, s: number): void {
    const n = this.entries.length;
    const gap = Math.min(20 * s, (this.scale.width * 0.7) / n);
    for (let i = 0; i < n; i++) {
      this.add.circle(cx + (i - (n - 1) / 2) * gap, y, i === this.selectedIndex ? 6 * s : 4 * s, i === this.selectedIndex ? PALETTE.laneAccent : PALETTE.trackLine, 1).setDepth(9);
    }
  }

  private setupDrag(w: number): void {
    const threshold = w * 0.11;
    const maxDrag = w * 0.3;
    this.input.on(Phaser.Input.Events.POINTER_DOWN, (p: Phaser.Input.Pointer) => { if (!this.switching) { this.dragging = true; this.startX = p.x; } });
    this.input.on(Phaser.Input.Events.POINTER_MOVE, (p: Phaser.Input.Pointer) => { if (this.dragging) this.dragDX = Phaser.Math.Clamp(p.x - this.startX, -maxDrag, maxDrag); });
    const release = (): void => {
      if (!this.dragging) return;
      this.dragging = false;
      if (this.dragDX <= -threshold) this.step(1);
      else if (this.dragDX >= threshold) this.step(-1);
      else this.dragDX = 0;
    };
    this.input.on(Phaser.Input.Events.POINTER_UP, release);
    this.input.on(Phaser.Input.Events.GAMEOBJECT_POINTER_UP, release);
  }

  private step(dir: number): void {
    if (this.switching) return;
    this.switching = true;
    AudioEngine.resume();
    AudioEngine.tap(0.3);
    const n = this.entries.length;
    const next = (this.selectedIndex + dir + n) % n;
    this.cameras.main.fadeOut(110, 11, 16, 32);
    this.cameras.main.once(Phaser.Cameras.Scene2D.Events.FADE_OUT_COMPLETE, () => this.scene.restart({ selectedIndex: next, filter: this.filter }));
  }

  private choose(): void {
    if (this.switching) return;
    const cur = this.entries[this.selectedIndex]!;
    if (cur.locked) { this.cameras.main.flash(150, 89, 210, 255); return; }
    this.scene.start('RosterSelect', { disciplineId: cur.id });
  }

  update(): void {
    if (!this.dragging && this.dragDX !== 0 && !this.switching) {
      this.dragDX = Phaser.Math.Linear(this.dragDX, 0, 0.2);
      if (Math.abs(this.dragDX) < 0.5) this.dragDX = 0;
    }
    for (const slot of this.slots) slot.container.x = slot.baseX + this.dragDX;
  }
}
